import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../utils/constants";
import { useAuth } from "../../utils/context";
import "./AdminDashboard.css";

const DEFAULT_PAGE_SIZE = 25;

const AdminDashboard = () => {
  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [summary, setSummary] = useState(null);
  const [instituteOptions, setInstituteOptions] = useState([]);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [instituteFilter, setInstituteFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isFetchingStudents, setIsFetchingStudents] = useState(true);
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
  const [activeUserId, setActiveUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [auth, setAuth] = useAuth();
  const navigate = useNavigate();

  const prevDebouncedSearch = useRef("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 450);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (prevDebouncedSearch.current !== debouncedSearch) {
      prevDebouncedSearch.current = debouncedSearch;
      setCurrentPage(1);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/students/institutes`);
        if (!cancelled && res.data?.success) {
          setInstituteOptions(res.data.institutes || []);
        }
      } catch {
        /* optional: institutes list is non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchStudentPage = useCallback(async () => {
    setIsFetchingStudents(true);
    setError(null);

    try {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (instituteFilter) {
        params.institute = instituteFilter;
      }

      const response = await axios.get(`${API}/admin/students`, { params });

      if (response.data?.success) {
        setStudents(response.data.students || []);
        const pag = response.data.pagination;
        if (pag) {
          setPagination({
            page: pag.page ?? currentPage,
            limit: pag.limit ?? pageSize,
            total: pag.total ?? 0,
            totalPages: Math.max(1, pag.totalPages ?? 1),
          });
        }
        if (response.data.summary) {
          setSummary(response.data.summary);
        }
      } else {
        setError(response.data?.message || "Failed to load students.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load students for admin."
      );
    } finally {
      setIsFetchingStudents(false);
      setHasCompletedInitialLoad(true);
    }
  }, [currentPage, pageSize, debouncedSearch, instituteFilter]);

  useEffect(() => {
    fetchStudentPage();
  }, [fetchStudentPage]);

  useEffect(() => {
    if (isFetchingStudents) {
      return;
    }
    const maxPage = pagination.totalPages || 1;
    if (pagination.total > 0 && currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [
    isFetchingStudents,
    pagination.total,
    pagination.totalPages,
    currentPage,
  ]);

  const handleRefresh = () => {
    fetchStudentPage();
  };

  const handleInstituteChange = (e) => {
    setInstituteFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const goToPage = (nextPage) => {
    const p = Math.min(
      Math.max(1, nextPage),
      pagination.totalPages || 1
    );
    setCurrentPage(p);
  };

  const summarySafe = useMemo(
    () =>
      summary || {
        totalStudents: 0,
        activeCount: 0,
        disabledCount: 0,
        instituteCount: 0,
        totalSubjects: 0,
      },
    [summary]
  );

  const showInitialLoader = !hasCompletedInitialLoad;
  const hasSearch = search.trim().length > 0;
  const hasActiveFilters = hasSearch || Boolean(instituteFilter);

  const rangeStart =
    pagination.total === 0
      ? 0
      : (pagination.page - 1) * pagination.limit + 1;
  const rangeEnd =
    pagination.total === 0
      ? 0
      : Math.min(pagination.page * pagination.limit, pagination.total);

  const handleImpersonate = async (userId) => {
    if (!userId) return;

    setLoading(true);
    setActiveUserId(userId);
    setError(null);

    try {
      const response = await axios.post(`${API}/admin/impersonate/${userId}`);

      if (response.data.success) {
        const currentToken = auth.accessToken?.token || auth.accessToken;
        localStorage.setItem("adminTokenBackup", currentToken);
        localStorage.setItem("adminUserBackup", JSON.stringify(auth.user));
        localStorage.setItem("isImpersonating", "true");

        const newAuthData = {
          user: response.data.user,
          accessToken: {
            token: response.data.accessToken,
            expiry:
              Date.now() +
              (response.data.impersonation?.expiresInMs ||
                24 * 60 * 60 * 1000),
          },
        };

        setAuth(newAuthData);
        localStorage.setItem("auth", JSON.stringify(newAuthData));
        navigate("/", { replace: true });
      } else {
        setError(response.data.message || "Failed to start impersonation");
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          "An error occurred during impersonation."
      );
    } finally {
      setLoading(false);
      setActiveUserId("");
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    return new Date(dateValue).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getRelativeUpdateText = (dateValue) => {
    if (!dateValue) return "No recent activity";

    const now = new Date();
    const updatedAt = new Date(dateValue);
    const diffHours = Math.max(0, Math.floor((now - updatedAt) / 36e5));

    if (diffHours < 1) return "Updated recently";
    if (diffHours < 24) return `Updated ${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `Updated ${diffDays}d ago`;
  };

  const renderStudentAvatar = (student) => {
    if (student.profilePic) {
      return (
        <img
          src={student.profilePic}
          alt=""
          className="admin-dashboard__avatar"
        />
      );
    }

    return (
      <div className="admin-dashboard__avatar-placeholder" aria-hidden>
        {(student.name || "S").charAt(0).toUpperCase()}
      </div>
    );
  };

  if (showInitialLoader) {
    return (
      <div className="admin-dashboard-loader" aria-busy="true">
        <div className="admin-dashboard-loader__top">
          <button
            type="button"
            className="admin-dashboard__back-btn admin-dashboard__back-btn--on-loader"
            onClick={() => navigate("/")}
          >
            ← Back to home
          </button>
        </div>
        <div className="admin-dashboard-loader__backdrop" />
        <div className="admin-dashboard-loader__card">
          <div className="admin-dashboard-loader__pulse" />
          <div
            className="admin-dashboard__spinner admin-dashboard__spinner--lg"
            role="status"
            aria-label="Loading"
          />
          <div>
            <p className="admin-dashboard-loader__eyebrow">
              Preparing control center
            </p>
            <p className="admin-dashboard-loader__title">Loading students</p>
            <p className="admin-dashboard-loader__hint">
              Please wait while we fetch the latest student list and admin
              actions.
            </p>
          </div>
          <div className="admin-dashboard-loader__skeletons" aria-hidden>
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard__inner">
        <div className="admin-dashboard__back-row">
          <button
            type="button"
            className="admin-dashboard__back-btn"
            onClick={() => navigate("/")}
          >
            ← Back to home
          </button>
        </div>
        <header className="admin-dashboard__hero admin-dashboard__hero--intro">
          <div className="admin-dashboard__hero-copy">
            <span className="admin-dashboard__eyebrow">Admin control center</span>
            <h1 className="admin-dashboard__title">Student Access Dashboard</h1>
          </div>
        </header>

        <section className="admin-dashboard__stats" aria-label="Dashboard summary">
          <article className="admin-dashboard__stat-card admin-dashboard__stat-card--primary">
            <span className="admin-dashboard__stat-label">Total students</span>
            <strong className="admin-dashboard__stat-value">
              {summarySafe.totalStudents}
            </strong>
            <span className="admin-dashboard__stat-foot">
              All non-admin accounts in the system
            </span>
          </article>

          <article className="admin-dashboard__stat-card">
            <span className="admin-dashboard__stat-label">Active accounts</span>
            <strong className="admin-dashboard__stat-value">
              {summarySafe.activeCount}
            </strong>
            <span className="admin-dashboard__stat-foot">
              Ready for secure impersonation
            </span>
          </article>

          <article className="admin-dashboard__stat-card">
            <span className="admin-dashboard__stat-label">Disabled accounts</span>
            <strong className="admin-dashboard__stat-value">
              {summarySafe.disabledCount}
            </strong>
            <span className="admin-dashboard__stat-foot">
              Blocked from admin login as student
            </span>
          </article>

          <article className="admin-dashboard__stat-card">
            <span className="admin-dashboard__stat-label">Institutes covered</span>
            <strong className="admin-dashboard__stat-value">
              {summarySafe.instituteCount}
            </strong>
            <span className="admin-dashboard__stat-foot">
              Across {summarySafe.totalSubjects} assigned subject entries
            </span>
          </article>
        </section>

        {error && (
          <div className="admin-dashboard__error" role="alert">
            <div>
              <strong className="admin-dashboard__error-title">
                Unable to complete admin action
              </strong>
              <p className="admin-dashboard__error-text">{error}</p>
            </div>
            <button
              type="button"
              className="admin-dashboard__error-action"
              onClick={handleRefresh}
            >
              Try again
            </button>
          </div>
        )}

        <section className="admin-dashboard__directory" aria-label="Students">
          <div className="admin-dashboard__section-head">
            <div>
              <h2 className="admin-dashboard__section-title">Student directory</h2>
            </div>
            <span className="admin-dashboard__table-count">
              {pagination.total} match{pagination.total !== 1 ? "es" : ""}
            </span>
          </div>

          <div className="admin-dashboard__directory-toolbar">
            <div className="admin-dashboard__toolbar">
              <label className="admin-dashboard__search-wrap">
                <span className="admin-dashboard__search-icon" aria-hidden>
                  Search
                </span>
                <input
                  type="search"
                  className="admin-dashboard__search"
                  placeholder="Search by name, email, institute or contact"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search students"
                />
                {hasSearch ? (
                  <button
                    type="button"
                    className="admin-dashboard__search-clear"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    Clear
                  </button>
                ) : null}
              </label>

              <div className="admin-dashboard__filter-group">
                <label
                  className="admin-dashboard__filter-label"
                  htmlFor="admin-institute-filter"
                >
                  Institute
                </label>
                <select
                  id="admin-institute-filter"
                  className="admin-dashboard__filter-select"
                  value={instituteFilter}
                  onChange={handleInstituteChange}
                  aria-label="Filter by institute"
                >
                  <option value="">All institutes</option>
                  <option value="__none__">No institute listed</option>
                  {instituteOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className="admin-dashboard__btn-refresh"
                onClick={handleRefresh}
                disabled={isFetchingStudents}
              >
                {isFetchingStudents ? "Refreshing..." : "Refresh list"}
              </button>
            </div>
          </div>

          {isFetchingStudents ? (
            <div className="admin-dashboard__content-loading" aria-busy="true">
              <div className="admin-dashboard__spinner" aria-hidden />
              <p>Loading student list...</p>
            </div>
          ) : students.length === 0 ? (
            <div className="admin-dashboard__table-empty">
              <strong>No students found</strong>
              <p>
                {hasActiveFilters
                  ? "Try different search text or institute, or clear filters."
                  : "No student records are available yet."}
              </p>
            </div>
          ) : (
            <div className="admin-dashboard__directory-results">
              <div className="admin-dashboard__mobile-list">
                {students.map((student) => {
                  const isRowLoading =
                    loading && activeUserId === student._id;

                  return (
                    <article
                      key={student._id}
                      className="admin-dashboard__student-card"
                    >
                      <div className="admin-dashboard__student-card-top">
                        <div className="admin-dashboard__student">
                          {renderStudentAvatar(student)}
                          <div>
                            <div className="admin-dashboard__student-name">
                              {student.name || "Unnamed student"}
                            </div>
                            <div className="admin-dashboard__student-email">
                              {student.email || "No email"}
                            </div>
                          </div>
                        </div>

                        <span
                          className={
                            student.accountStatus
                              ? "admin-dashboard__badge admin-dashboard__badge--active"
                              : "admin-dashboard__badge admin-dashboard__badge--disabled"
                          }
                        >
                          {student.accountStatus ? "Active" : "Disabled"}
                        </span>
                      </div>

                      <div className="admin-dashboard__student-grid">
                        <div>
                          <span className="admin-dashboard__mini-label">
                            Contact
                          </span>
                          <span className="admin-dashboard__mini-value">
                            {student.contact || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="admin-dashboard__mini-label">
                            Institute
                          </span>
                          <span className="admin-dashboard__mini-value">
                            {student.institute || "N/A"}
                          </span>
                        </div>
                        <div>
                          <span className="admin-dashboard__mini-label">
                            Subjects
                          </span>
                          <span className="admin-dashboard__mini-value">
                            {student.selectedSubjectsCount}
                          </span>
                        </div>
                        <div>
                          <span className="admin-dashboard__mini-label">
                            Joined
                          </span>
                          <span className="admin-dashboard__mini-value">
                            {formatDate(student.createdAt)}
                          </span>
                        </div>
                      </div>

                      <div className="admin-dashboard__student-card-footer">
                        <span className="admin-dashboard__student-updated">
                          {getRelativeUpdateText(student.updatedAt)}
                        </span>

                        <button
                          type="button"
                          className={[
                            "admin-dashboard__btn-login",
                            isRowLoading &&
                              "admin-dashboard__btn-login--loading",
                            !student.accountStatus &&
                              "admin-dashboard__btn-login--blocked",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onClick={() => handleImpersonate(student._id)}
                          disabled={loading || !student.accountStatus}
                        >
                          {isRowLoading ? "Opening..." : "Login as student"}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="admin-dashboard__table-card">
                <div className="admin-dashboard__table-wrap">
                  <table className="admin-dashboard__table">
                    <thead>
                      <tr>
                        <th scope="col">Student</th>
                        <th scope="col">Contact</th>
                        <th scope="col">Institute</th>
                        <th scope="col">Subjects</th>
                        <th scope="col">Joined</th>
                        <th scope="col">Last update</th>
                        <th scope="col">Status</th>
                        <th
                          scope="col"
                          className="admin-dashboard__th--action"
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => {
                        const isRowLoading =
                          loading && activeUserId === student._id;
                        const loginClass = [
                          "admin-dashboard__btn-login",
                          isRowLoading &&
                            "admin-dashboard__btn-login--loading",
                          !student.accountStatus &&
                            "admin-dashboard__btn-login--blocked",
                        ]
                          .filter(Boolean)
                          .join(" ");

                        return (
                          <tr key={student._id}>
                            <td>
                              <div className="admin-dashboard__student">
                                {renderStudentAvatar(student)}
                                <div>
                                  <div className="admin-dashboard__student-name">
                                    {student.name || "Unnamed student"}
                                  </div>
                                  <div className="admin-dashboard__student-email">
                                    {student.email || "No email"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="admin-dashboard__cell--muted">
                              {student.contact || "N/A"}
                            </td>
                            <td className="admin-dashboard__cell--muted">
                              <span className="admin-dashboard__cell--truncate">
                                {student.institute || "N/A"}
                              </span>
                            </td>
                            <td className="admin-dashboard__cell--muted">
                              {student.selectedSubjectsCount}
                            </td>
                            <td className="admin-dashboard__cell--muted">
                              {formatDate(student.createdAt)}
                            </td>
                            <td className="admin-dashboard__cell--muted">
                              {getRelativeUpdateText(student.updatedAt)}
                            </td>
                            <td>
                              <span
                                className={
                                  student.accountStatus
                                    ? "admin-dashboard__badge admin-dashboard__badge--active"
                                    : "admin-dashboard__badge admin-dashboard__badge--disabled"
                                }
                              >
                                {student.accountStatus ? "Active" : "Disabled"}
                              </span>
                            </td>
                            <td className="admin-dashboard__td-action">
                              <button
                                type="button"
                                className={loginClass}
                                onClick={() => handleImpersonate(student._id)}
                                disabled={loading || !student.accountStatus}
                              >
                                {isRowLoading ? "Opening..." : "Login"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <footer
            className="admin-dashboard__pagination-footer"
            aria-label="Pagination and list options"
          >
            <div className="admin-dashboard__pagination-size">
              <label htmlFor="admin-page-size">Rows per page</label>
              <select
                id="admin-page-size"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <p className="admin-dashboard__pagination-range">
              Showing{" "}
              <strong>
                {rangeStart}–{rangeEnd}
              </strong>{" "}
              of <strong>{pagination.total}</strong>
            </p>
            <nav
              className="admin-dashboard__pagination-nav"
              aria-label="Student list pages"
            >
              <button
                type="button"
                className="admin-dashboard__pagination-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={isFetchingStudents || currentPage <= 1}
              >
                Previous
              </button>
              <span className="admin-dashboard__pagination-status">
                Page <strong>{pagination.page}</strong> of{" "}
                <strong>{pagination.totalPages}</strong>
              </span>
              <button
                type="button"
                className="admin-dashboard__pagination-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={
                  isFetchingStudents ||
                  currentPage >= pagination.totalPages
                }
              >
                Next
              </button>
            </nav>
          </footer>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
